import { theme as antTheme, ThemeConfig } from 'antd';
import { ResolvedTheme } from 'types';
import { themeObject } from './themeVariables';

/**
 * Keeps Ant Design components and body-level portals on the same resolved theme.
 * Component overrides are limited to the existing ProSinergia identity; surfaces,
 * content colors, states and interaction colors come from semantic theme tokens.
 */
export const getAntDesignTheme = (
  resolvedTheme: ResolvedTheme
): ThemeConfig => {
  const { semantic, ...palette } = themeObject[resolvedTheme];

  return {
    algorithm:
      resolvedTheme === 'dark'
        ? antTheme.darkAlgorithm
        : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: semantic.brandPrimary,
      colorPrimaryHover: semantic.brandPrimaryHover,
      colorPrimaryActive: semantic.brandPrimaryHover,
      colorPrimaryBg: semantic.surfaceSelected,
      colorPrimaryBgHover: semantic.surfaceHover,
      colorInfo: palette.primary,
      colorInfoText: semantic.statusInfoText,
      colorInfoBg: semantic.statusInfoSurface,
      colorInfoBorder: semantic.statusInfoBorder,
      colorSuccess: palette.success,
      colorSuccessText: semantic.statusSuccessText,
      colorSuccessBg: semantic.statusSuccessSurface,
      colorSuccessBorder: semantic.statusSuccessBorder,
      colorWarning: palette.warning,
      colorWarningText: semantic.statusWarningText,
      colorWarningBg: semantic.statusWarningSurface,
      colorWarningBorder: semantic.statusWarningBorder,
      colorError: palette.error,
      colorErrorText: semantic.statusErrorText,
      colorErrorBg: semantic.statusErrorSurface,
      colorErrorBorder: semantic.statusErrorBorder,
      colorBgBase: semantic.surfacePage,
      colorBgLayout: semantic.surfacePage,
      colorBgContainer: semantic.surfaceContainer,
      colorBgElevated: semantic.surfaceElevated,
      colorBgSpotlight: semantic.navigationSurface,
      colorTextBase: semantic.textPrimary,
      colorText: semantic.textPrimary,
      colorTextSecondary: semantic.textSecondary,
      colorTextTertiary: semantic.textMuted,
      colorTextQuaternary: semantic.textMuted,
      colorTextDisabled: semantic.textDisabled,
      colorTextLightSolid: semantic.textOnBrand,
      colorBorder: semantic.borderStrong,
      colorBorderSecondary: semantic.borderSubtle,
      colorFill: semantic.surfaceSelected,
      colorFillSecondary: semantic.surfaceMuted,
      colorFillTertiary: semantic.surfaceHover,
      colorFillQuaternary: semantic.surfaceMuted,
      controlItemBgHover: semantic.surfaceHover,
      controlItemBgActive: semantic.surfaceSelected,
      controlOutline: semantic.focusRing,
      colorBgContainerDisabled: semantic.surfaceDisabled,
      colorLink: semantic.brandPrimary,
      colorLinkHover: semantic.brandPrimaryHover,
      colorLinkActive: semantic.brandPrimaryHover,
      boxShadow: semantic.shadowElevated,
      boxShadowSecondary: semantic.shadowOverlay,
    },
    components: {
      Button: {
        borderRadius: 10,
        colorPrimary: semantic.brandPrimary,
        colorPrimaryHover: semantic.brandPrimaryHover,
        defaultColor: semantic.statusErrorText,
        defaultHoverColor: semantic.statusErrorBorder,
        defaultBorderColor: semantic.statusErrorText,
        defaultHoverBorderColor: semantic.statusErrorBorder,
        colorBgContainerDisabled: semantic.surfaceDisabled,
        colorTextDisabled: semantic.textDisabled,
        borderColorDisabled: semantic.borderSubtle,
      },
      Layout: {
        bodyBg: semantic.surfacePage,
        headerBg: semantic.navigationSurface,
        siderBg: semantic.navigationSurface,
      },
      Modal: {
        contentBg: semantic.surfaceElevated,
        headerBg: semantic.surfaceElevated,
        titleColor: semantic.brandPrimary,
      },
      Table: {
        borderColor: semantic.borderSubtle,
        headerBg: semantic.surfaceMuted,
        headerColor: semantic.textPrimary,
        headerSplitColor: semantic.borderSubtle,
        rowHoverBg: semantic.surfaceHover,
      },
      Card: {
        colorBgContainer: semantic.surfaceContainer,
        colorBorderSecondary: semantic.borderSubtle,
        headerBg: 'transparent',
      },
      Input: {
        activeBorderColor: semantic.brandPrimary,
        activeShadow: `0 0 0 2px ${semantic.surfaceSelected}`,
        hoverBorderColor: semantic.brandPrimary,
      },
      Select: {
        optionActiveBg: semantic.surfaceHover,
        optionSelectedBg: semantic.surfaceSelected,
        selectorBg: semantic.surfaceContainer,
      },
      DatePicker: {
        activeBorderColor: semantic.brandPrimary,
        activeShadow: `0 0 0 2px ${semantic.surfaceSelected}`,
        hoverBorderColor: semantic.brandPrimary,
      },
      Calendar: {
        fullBg: semantic.surfaceContainer,
        fullPanelBg: semantic.surfaceContainer,
        itemActiveBg: semantic.surfaceSelected,
      },
      Menu: {
        darkItemBg: semantic.navigationSurface,
        darkSubMenuItemBg: semantic.navigationSurface,
        darkItemColor: semantic.navigationText,
        darkItemHoverColor: semantic.brandAccent,
        darkItemSelectedColor: semantic.brandAccent,
      },
    },
  };
};
